// calibrationLogBookForm/calibrationLogBookSectionActions.js
// Calibration log section actions for the standalone Calibration Log Book.
// Mirrors makeCalibrationLogActions (sampling event) but uses the generic CalibrationLogBookEntry
// model and the active book's entries array.

import { CalibrationLogBookEntry } from "../../../models/CalibrationLogBook/CalibrationLogBookEntry";

// api shim provided by the parent factory:
// - getCurrentParent(): CalibrationLogBook | null
// - bump(patch?): void
// - bumpRevision(): void
// - setCalibrationLogUi(patch): void
export const makeCalibrationLogBookSectionActions = (api) => ({
  openModal: (headerString, isAddMode = false) => {
    api.setCalibrationLogUi({
      isModalOpen: true,
      modalHeaderString: headerString,
      isAddMode: isAddMode,
    });
    api.bump();
  },

  setCurrentLogId: (logId) => {
    api.setCalibrationLogUi({ currentLogId: logId });
    api.bump();
  },

  closeModal: () => {
    api.setCalibrationLogUi({
      isModalOpen: false,
      currentLogId: null,
      modalHeaderString: "",
      isAddMode: false,
    });
    api.bump();
  },

  openDeleteConfirm: (logId) => {
    api.setCalibrationLogUi({ isDeleteConfirmOpen: true, logToDelete: logId });
    api.bump();
  },

  closeDeleteConfirm: () => {
    api.setCalibrationLogUi({ isDeleteConfirmOpen: false, logToDelete: null });
    api.bump();
  },

  // ============================================================================
  // COMMIT TEMP STATE TO ACTUAL DATA AND SERVER
  // ============================================================================
  commitTempCalibrationLogs: async (tempState, _parentData, emitPatch) => {
    const book = api.getCurrentParent();
    if (!book) {
      return { ok: false, error: "No active calibration log book" };
    }

    const { sharedFields, logs, existingLogIds } = tempState;
    const errors = [];

    for (const [logId, logData] of Object.entries(logs)) {
      const isExisting = existingLogIds.includes(logId);
      let log;

      const existingLogInBook = book.entries.find((l) => l.id === logId);

      if (isExisting || existingLogInBook) {
        log = existingLogInBook;
        if (!log) {
          errors.push(`Log ${logId} not found`);
          continue;
        }
      } else {
        log = new CalibrationLogBookEntry({
          id: logId,
          book_id: book.id,
          employee_id: sharedFields.employeeId,
          employee_name: sharedFields.employeeName,
          created_at: new Date(),
          updated_at: new Date(),
        });
        book.entries.push(log);
      }

      // Apply shared fields
      log.setCalibrationDate(sharedFields.calibrationDate);
      log.setCalibrationTime(sharedFields.calibrationTime);
      log.setInstrument(sharedFields.instrument);
      log.setEmployeeId(sharedFields.employeeId);
      log.setEmployeeName(sharedFields.employeeName);

      // Apply log-specific fields
      log.setFieldParameter(logData.fieldParameter);
      log.setStandardUsed(logData.standardUsed);
      log.setVerification(logData.verification);
      log.setUnits(logData.units);
      log.setPassFail(logData.passFail);

      const action = isExisting ? "update" : "add";
      const patch = log.toPatch(action);

      if (typeof emitPatch === "function") {
        try {
          const result = await emitPatch(patch);
          const isSuccess =
            result === true || result?.success === true || result?.ok === true;
          if (!isSuccess && result !== undefined) {
            const errorMsg =
              result?.error || result?.message || "Server returned an error";
            errors.push(`Failed to save log: ${errorMsg}`);
          }
        } catch (err) {
          errors.push(`Error saving log: ${err.message || err}`);
        }
      }
    }

    api.bumpRevision();

    if (errors.length > 0) {
      console.warn("Calibration log book save warnings:", errors);
      return { ok: true, warnings: errors };
    }

    return { ok: true };
  },

  // ============================================================================
  // REMOVE LOG (from card list)
  // ============================================================================
  removeCalibrationLog: async (logId, emitPatch) => {
    const book = api.getCurrentParent();
    if (!book?.id) return { ok: false, error: "No active calibration log book" };
    book.removeEntry(logId);
    const patch = {
      patch_type: "calibration_log_book_entry",
      action: "remove",
      id: logId,
    };
    if (typeof emitPatch === "function") {
      const result = await emitPatch(patch);
      api.bumpRevision();
      if (!result.success) {
        return { ok: false, error: result.error };
      }
      return { ok: true };
    }
    api.bumpRevision();
    return { ok: false, error: "No emitPatch function provided" };
  },

  // ============================================================================
  // WEBSOCKET BROADCAST HANDLER
  // ============================================================================
  handleBroadcast: (patch, applyPatchToObject) => {
    const book = api.getCurrentParent();
    if (!book) return;

    const { id, action } = patch;
    const logs = book.entries || [];

    if (action === "add") {
      const existingLog = logs.find((l) => l.id === id);
      if (existingLog) {
        applyPatchToObject(existingLog, patch, [
          "id",
          "action",
          "book_id",
          "calibration_log_book_id",
        ]);
        api.bumpRevision();
        return;
      }
      const newLog = new CalibrationLogBookEntry(patch);
      logs.push(newLog);
      api.bumpRevision();
      return;
    }

    if (action === "remove" || action === "delete") {
      const idx = logs.findIndex((l) => l.id === id);
      if (idx >= 0) {
        logs.splice(idx, 1);
        api.bumpRevision();
      }
      return;
    }

    // update
    const log = logs.find((l) => l.id === id);
    if (!log) {
      const newLog = new CalibrationLogBookEntry(patch);
      logs.push(newLog);
      api.bump();
      return;
    }
    applyPatchToObject(log, patch, [
      "id",
      "action",
      "book_id",
      "calibration_log_book_id",
    ]);
    api.bump();
  },
});
